import { useRaspiStore } from '@/store/raspiStore';
import { useChamberStore } from '@/store/chamberStore';
import { useCameraStore } from '@/store/cameraStore';
import { useExperimentStore } from '@/store/experimentStore';
import { useSystemHealthStore } from '@/store/systemHealthStore';
import { useLogsStore } from '@/store/logsStore';

import type { RaspiDevice, Camera, Chamber } from '@/types/raspi';
import type { Experiment } from '@/types/experiment';

interface WebSocketMessage {
  type: string;
  timestamp: string;
  data?: any;
  raspi_id?: string;
  new_entries?: any[];
}

// ✅ WebSocket Data Validation Layer
class WebSocketDataValidator {
  static validateRaspiUpdate(data: any): { valid: boolean; entityId: string; validatedData: Partial<RaspiDevice> } {
    const entityId = data.raspi_id;
    
    // ✅ Respect hardware discovery - if entity doesn't exist, trigger discovery
    if (!entityId) {
      return { valid: false, entityId: '', validatedData: {} };
    }
    
    // Validate required fields are present
    if (!data.raspi_status || !data.raspi_health_status || !data.raspi_alert) {
      return { valid: false, entityId: '', validatedData: {} };
    }
    
    const validatedData: Partial<RaspiDevice> = {
      raspi_status: data.raspi_status,
      last_seen: data.last_seen || new Date().toISOString(),
      raspi_health_status: data.raspi_health_status,
      raspi_alert: data.raspi_alert,
      raspi_alert_description: data.raspi_alert_description || '',
      raspi_health_message: data.raspi_health_message,
      chamber_id: data.chamber_id || '',
      camera_ids: data.camera_ids || [],
      exp_id: data.exp_id || '',
      // Memory data from heartbeat/raspi_info - use correct database field names
      raspi_free_ram_gb: data.raspi_free_ram_gb,
      raspi_total_ram_gb: data.raspi_total_ram_gb,
      raspi_free_sd_gb: data.raspi_free_sd_gb,
      raspi_total_sd_gb: data.raspi_total_sd_gb,
      // Additional fields from database
      raspi_ip: data.raspi_ip,
      raspi_mac: data.raspi_mac,
      raspi_software_version: data.raspi_software_version,
      last_update: data.last_update,
    };
    
    return { valid: true, entityId, validatedData };
  }

  static validateChamberUpdate(data: any): { valid: boolean; entityId: string; validatedData: Partial<Chamber> } {
    const entityId = data.chamber_id;

    if (!entityId) {
      return { valid: false, entityId: '', validatedData: {} };
    }

    // Validate required fields are present (allow 0 values for counters)
    if (data.chamber_meas_counter === undefined || data.chamber_meas_total === undefined || !data.chamber_status || !data.chamber_alert) {
      return { valid: false, entityId: '', validatedData: {} };
    }

    const validatedData: Partial<Chamber> = {
      raspi_id: data.raspi_id,
      chamber_meas_counter: data.chamber_meas_counter,
      chamber_meas_total: data.chamber_meas_total,
      chamber_status: data.chamber_status,
      chamber_alert: data.chamber_alert,
      chamber_alert_description: data.chamber_alert_description || '',
      // Additional fields from database
      chamber_arduino_port: data.chamber_arduino_port,
      baudrate: data.baudrate,
      chamber_software_version: data.chamber_software_version,
      csv_file_path: data.csv_file_path,
      // Live control-sensor readings (for the board readout + readiness).
      chamber_pressure: data.chamber_pressure,
      chamber_temperature: data.chamber_temperature,
      last_update: data.last_update,
    };

    return { valid: true, entityId, validatedData };
  }

  static validateCameraUpdate(data: any): { valid: boolean; entityId: string; validatedData: Partial<Camera> } {
    const entityId = data.camera_id;

    if (!entityId) {
      return { valid: false, entityId: '', validatedData: {} };
    }

    // Validate required fields are present (allow 0 values for counters)
    if (data.camera_image_counter === undefined || data.camera_image_total === undefined || !data.camera_status || !data.camera_alert) {
      return { valid: false, entityId: '', validatedData: {} };
    }

    const validatedData: Partial<Camera> = {
      raspi_id: data.raspi_id,
      camera_image_counter: data.camera_image_counter,
      camera_image_total: data.camera_image_total,
      camera_status: data.camera_status,
      camera_alert: data.camera_alert,
      camera_alert_description: data.camera_alert_description || '',
      // Additional fields from database
      camera_gauge_id: data.camera_gauge_id,
      camera_led_id: data.camera_led_id,
      image_dir_path: data.image_dir_path,
      camera_model: data.camera_model,
      camera_resolution: data.camera_resolution,
      last_update: data.last_update,
    };

    return { valid: true, entityId, validatedData };
  }

  static validateExperimentUpdate(data: any): { valid: boolean; entityId: string; validatedData: Partial<Experiment> } {
    const entityId = data.exp_id;
    
    if (!entityId) {
      return { valid: false, entityId: '', validatedData: {} };
    }
    
    // Validate required fields are present
    if (!data.exp_status || !data.exp_alert) {
      return { valid: false, entityId: '', validatedData: {} };
    }
    
    // Simplified validation - focus on essential message structure
    // Comprehensive validation happens at UI level during run confirmation
    
    const validatedData: Partial<Experiment> = {
      exp_status: data.exp_status,
      start_time: data.start_time,
      end_time: data.end_time,
      exp_status_time: data.exp_status_time,
      exp_time_total: data.exp_time_total,
      exp_time_counter: data.exp_time_counter,
      exp_points_total: data.exp_points_total,
      exp_points_counter: data.exp_points_counter,
      exp_expect_total: data.exp_expect_total,
      exp_expect_counter: data.exp_expect_counter,
      num_profiles: data.num_profiles,
      test_id: data.test_id,
      profile_id: data.profile_id,
      profile_file_path: data.profile_file_path,
      raspi_id: data.raspi_id,
      chamber_id: data.chamber_id,
      chamber_enabled: data.chamber_enabled,
      cameras_enabled: data.cameras_enabled,
      exp_alert: data.exp_alert,
      exp_alert_description: data.exp_alert_description,
      pressure_steps: data.pressure_steps,
      temperature_steps: data.temperature_steps,
      time_steps: data.time_steps,
      measurement_intervals: data.measurement_intervals,
      time_interval_steps: data.time_interval_steps,
    };
    
    return { valid: true, entityId, validatedData };
  }
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private isConnecting = false;
  private messageHandlers: Map<string, (message: WebSocketMessage) => void> = new Map();
  private cachedWsUrl: string | null = null;
  private healthCheckInterval: NodeJS.Timeout | null = null;
  private currentRaspiId: string | null = null; // Track current RASPI context for log filtering
  private wasDisconnected = false; // Track if we were disconnected to trigger sync on reconnect

  constructor() {
    this.setupMessageHandlers();
    this.startHealthMonitoring();
  }

  private setupMessageHandlers() {
    // ✅ Raspi updates with validation
    this.messageHandlers.set('raspi_update', (message) => {
      const data = message.data;
      const validation = WebSocketDataValidator.validateRaspiUpdate(data);
      
      if (!validation.valid) {
        // Don't trigger recovery for invalid payloads - just ignore them
        return;
      }
      
      const store = useRaspiStore.getState();
      
      if (!store.allRaspis[validation.entityId]) {
        // Create new instance in store from WebSocket payload
        store.allRaspis[validation.entityId] = {
          raspi_id: validation.entityId,
          ...validation.validatedData
        } as any;
        // Optionally, trigger a UI update by setting the store
        useRaspiStore.setState({ allRaspis: { ...store.allRaspis } });
        return;
      }
      
      store.updateRaspiFromWebSocket(validation.entityId, validation.validatedData);
    });

    // ✅ Chamber updates with validation
    this.messageHandlers.set('chamber_update', (message) => {
      const data = message.data;
      const validation = WebSocketDataValidator.validateChamberUpdate(data);

      if (!validation.valid) {
        // Don't trigger recovery for invalid payloads - just ignore them
        return;
      }

      const store = useChamberStore.getState();

      if (!store.allChambers[validation.entityId]) {
        store.allChambers[validation.entityId] = {
          chamber_id: validation.entityId,
          ...validation.validatedData
        } as any;
        useChamberStore.setState({ allChambers: { ...store.allChambers } });
      } else {
        store.updateChamberFromWebSocket(validation.entityId, validation.validatedData);
      }

      // Back-fill parent RASPI's chamber_id so the visibility gate fires.
      // Without this, the chamber appears in chamberStore but the RASPI
      // record's chamber_id stays null until the next REST fetch — which
      // is the "chamber sometimes invisible" race the user was hitting.
      const raspiId: string | undefined = (validation.validatedData as any).raspi_id;
      if (raspiId) {
        const raspiStore = useRaspiStore.getState();
        const raspi = raspiStore.allRaspis[raspiId];
        if (raspi && raspi.chamber_id !== validation.entityId) {
          raspiStore.updateRaspiFromWebSocket(raspiId, { chamber_id: validation.entityId });
        }
      }
    });

    // ✅ Camera updates with validation
    this.messageHandlers.set('camera_update', (message) => {
      const data = message.data;
      const validation = WebSocketDataValidator.validateCameraUpdate(data);

      if (!validation.valid) {
        // Don't trigger recovery for invalid payloads - just ignore them
        return;
      }

      const store = useCameraStore.getState();

      if (!store.allCameras[validation.entityId]) {
        store.allCameras[validation.entityId] = {
          camera_id: validation.entityId,
          ...validation.validatedData
        } as any;
        useCameraStore.setState({ allCameras: { ...store.allCameras } });
      } else {
        store.updateCameraFromWebSocket(validation.entityId, validation.validatedData);
      }

      // Back-fill parent RASPI's camera_ids so the camera list renders
      // (mirror fix to the chamber backfill above).
      const raspiId: string | undefined = (validation.validatedData as any).raspi_id;
      if (raspiId) {
        const raspiStore = useRaspiStore.getState();
        const raspi = raspiStore.allRaspis[raspiId];
        if (raspi) {
          const existingIds = Array.isArray(raspi.camera_ids) ? raspi.camera_ids : [];
          if (!existingIds.includes(validation.entityId)) {
            raspiStore.updateRaspiFromWebSocket(raspiId, {
              camera_ids: [...existingIds, validation.entityId],
            });
          }
        }
      }
    });

    // ✅ Experiment updates with validation
    this.messageHandlers.set('experiment_update', (message) => {
      const data = message.data;
      const validation = WebSocketDataValidator.validateExperimentUpdate(data);
      
      if (!validation.valid) {
        // Don't trigger recovery for invalid payloads - just ignore them
        return;
      }
      
      const store = useExperimentStore.getState();
      
      if (!store.allExperiments[validation.entityId]) {
        store.allExperiments[validation.entityId] = {
          exp_id: validation.entityId,
          ...validation.validatedData
        } as any;
        useExperimentStore.setState({ allExperiments: { ...store.allExperiments } });
        return;
      }
      
      store.updateExperimentFromWebSocket(validation.entityId, validation.validatedData);
    });

    // Experiment deletion
    this.messageHandlers.set('experiment_deleted', (message) => {
      const data = message.data;
      const store = useExperimentStore.getState();
      // Remove from store directly (store-only removal)
      store.allExperiments = Object.fromEntries(
        Object.entries(store.allExperiments).filter(([expId]) => expId !== data.exp_id)
      );
      useExperimentStore.setState({ allExperiments: { ...store.allExperiments } });
    });

    // System health updates
    this.messageHandlers.set('system_health_update', (message) => {
      const data = message.data;
      const store = useSystemHealthStore.getState();
      store.updateHealthStatus(data.service, data.status);
    });

    // Alert updates
    this.messageHandlers.set('alert', (message) => {
      const data = message.data;
      
      // Validate required fields are present
      if (!data.source || !data.alert_level || !data.alert_message) {
        return;
      }
      
      const store = useSystemHealthStore.getState();
      store.addSystemAlert({
        alert_id: `${data.source}_${Date.now()}`, // Generate simple ID for compatibility
        source: data.source,
        alert_level: data.alert_level,
        alert_message: data.alert_message,
        timestamp: new Date().toISOString(),
        exp_id: data.exp_id,
      });
    });

    // Unified log updates (new IOPLogger format)
    this.messageHandlers.set('log_update', (message) => {
      const logsStore = useLogsStore.getState();
      // Fix: Look for new_entries in message.data, not directly in message
      const newEntries = message.data?.new_entries;
      if (newEntries && newEntries.length > 0) {
        const transformedEntries = newEntries.map((entry: any) => {
          // Parse the unified format: [timestamp] [origin] [level] [source] [message]
          const match = entry.raw_line?.match(/\[([^\]]+)\]\s+\[([^\]]+)\]\s+\[([^\]]+)\]\s+\[([^\]]+)\]\s+\[(.+)\]/);
          if (match) {
            return {
              timestamp: match[1],
              origin: match[2],
              level: match[3],
              source_component: match[4],
              message: match[5],
              raw_line: entry.raw_line
            };
          }
          // Fallback for old format
          return {
            timestamp: entry.timestamp,
            origin: entry.origin,
            level: entry.level,
            source_component: entry.source_component,
            message: entry.message,
            raw_line: entry.raw_line
          };
        });
        
        // Filter logs based on current RASPI context
        const filteredEntries = transformedEntries.filter((entry: any) => {
          // Always include system logs
          if (entry.origin === 'SYS') {
            return true;
          }
          
          // If we have a current RASPI context, only include logs for that RASPI
          if (this.currentRaspiId) {
            return entry.origin === this.currentRaspiId;
          }
          
          // If no RASPI context, include all RASPI logs (fallback behavior)
          return true;
        });
        
        // Route filtered logs: 'SYS' goes to hub frame, RASPI origins go to both hub and RASPI frames
        filteredEntries.forEach((entry: any) => {
          // Always add to hub frame (sysLogs)
          logsStore.addLogEntries([entry], 'sys');
          
          // If origin is not 'SYS', also add to RASPI frame
          if (entry.origin !== 'SYS') {
            logsStore.addLogEntries([entry], 'raspi');
          }
        });
      }
    });

    // Camera frame updates
    this.messageHandlers.set('camera_frame', (message) => {
      const data = message.data;
      
      // Validate required fields are present
      if (!data.camera_id || !data.frame_data || !data.timestamp) {
        return;
      }
      
      const store = useCameraStore.getState();
      store.handleCameraFrame({
        camera_id: data.camera_id,
        frame_data: data.frame_data,
        timestamp: data.timestamp,
        frame_size: data.frame_size
      });
    });

    // Connection established
    this.messageHandlers.set('connection_established', () => {
      // Connection established handler - no action needed
    });
  }

  // ✅ Implement Connection Health Monitoring
  private startHealthMonitoring() {
    this.healthCheckInterval = setInterval(() => {
      // Log connection status every 30 seconds for debugging
      this.logConnectionStatus();
      
      // Only attempt reconnection if we're not already connecting and connection is not open
      if (!this.isConnecting && this.ws?.readyState !== WebSocket.OPEN) {
        this.handleReconnect();
      }
    }, 30000); // Check every 30 seconds
  }

  private stopHealthMonitoring() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  // ✅ Enhanced Message Validation
  private validateWebSocketMessage(message: WebSocketMessage): boolean {
    if (!message.type || !message.timestamp) {
      console.error('Invalid WebSocket message: missing required fields');
      return false;
    }
    
    if (!this.messageHandlers.has(message.type)) {
      return false;
    }
    
    return true;
  }

  private async getWebSocketUrl(): Promise<string> {
    if (this.cachedWsUrl) {
      return this.cachedWsUrl;
    }

    // Use relative URL - let nginx handle the proxy
    // Convert HTTP to WS, HTTPS to WSS based on current protocol
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    this.cachedWsUrl = `${protocol}//${window.location.host}/api/ws`;
    return this.cachedWsUrl;
  }

  async connect(): Promise<void> {
    // Prevent multiple simultaneous connection attempts
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }
    
    if (this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    try {
      // Get WebSocket URL from backend preferences
      const wsUrl = await this.getWebSocketUrl();
      
      // Connect to WebSocket for all real-time updates
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        this.isConnecting = false;
        // Reset reconnect attempts when connection is successful
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        
        // ✅ Fix 1: Fetch all stores on reconnect to recover missed updates
        if (this.wasDisconnected) {
          // Fetch all stores to recover any missed updates during disconnection
          useRaspiStore.getState().fetchAllRaspis().catch(err => 
            console.error('Error fetching RASPIs on reconnect:', err)
          );
          useChamberStore.getState().fetchAllChambers().catch(err => 
            console.error('Error fetching Chambers on reconnect:', err)
          );
          useCameraStore.getState().fetchAllCameras().catch(err => 
            console.error('Error fetching Cameras on reconnect:', err)
          );
          useExperimentStore.getState().fetchAllExperiments().catch(err => 
            console.error('Error fetching Experiments on reconnect:', err)
          );
          
          this.wasDisconnected = false;
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          
          // ✅ Validate message before processing
          if (!this.validateWebSocketMessage(message)) {
            return;
          }
          
          this.handleMessage(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = () => {
        this.isConnecting = false;
        this.wasDisconnected = true; // ✅ Track disconnection for sync on reconnect
        this.handleReconnect();
      };

      this.ws.onerror = () => {
        this.isConnecting = false;
      };

    } catch (error) {
      // Don't log connection errors when hub is intentionally stopped
      if (!(error instanceof TypeError && error.message.includes('Failed to fetch'))) {
        console.error('Error connecting to WebSocket:', error);
      }
      this.isConnecting = false;
      this.handleReconnect();
    }
  }

  private handleMessage(message: WebSocketMessage) {
    const handler = this.messageHandlers.get(message.type);
    if (handler) {
      handler(message);
    }
  }

  private handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    this.reconnectAttempts++;

    setTimeout(() => {
      this.connect();
      // Exponential backoff with reasonable limits
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
    }, this.reconnectDelay);
  }

  disconnect(): void {
    this.stopHealthMonitoring();
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnecting');
      this.ws = null;
    }
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    // Clear cached URL to force re-fetch of preferences on next connection
    this.cachedWsUrl = null;
  }

  // Method to clear cached URL when preferences are updated
  clearCache(): void {
    this.cachedWsUrl = null;
  }

  // Camera stream subscription methods
  subscribeToCameraStream(cameraId: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'subscribe_camera_stream',
        camera_id: cameraId,
        timestamp: new Date().toISOString()
      }));
    }
  }

  unsubscribeFromCameraStream(cameraId: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'unsubscribe_camera_stream',
        camera_id: cameraId,
        timestamp: new Date().toISOString()
      }));
    }
  }

  send(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getConnectionStatus(): string {
    if (!this.ws) return 'disconnected';
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'closed';
      default:
        return 'unknown';
    }
  }

  // Method to get current connection status for debugging
  logConnectionStatus(): void {
    // Status logging removed for cleaner console output
  }

  // Set current RASPI context for log filtering
  setCurrentRaspiContext(raspiId: string | null): void {
    this.currentRaspiId = raspiId;
  }
}

// Create singleton instance
let websocketServiceInstance: WebSocketService | null = null;

export const websocketService = (() => {
  if (!websocketServiceInstance) {
    websocketServiceInstance = new WebSocketService();
  }
  return websocketServiceInstance;
})();

export default websocketService; 